import * as THREE from 'three';
import InputCommand from './InputCommand';
import ICanvasInputListener from '../logic/ICanvasInputListener';
import CommandIdEnum from '../enums/CommandIdEnums';
import Document from '../model/document/Document';
import { ElementTypeEnum } from '../elementTypeMappings';
import { getParentThreeProductByType } from '../utils/productUserDataUtils';
import SceneObjectData from '../model/sceneObjectData/SceneObjectData';
import { getSceneObjectDataFromObject3D } from '../utils/sceneObjectUtils';
import CommandStack from '../logic/CommandStack';
import { useServicesState } from '../store/servicesStateStore';
import createSceneObject from '../model/sceneObject/SceneObjectFactory';
import ISceneObject from '../model/sceneObject/ISceneObject';
import { UUID } from '../utils/typeDefinitions';
import { ProductMeshUserData } from '../interfaces/ProductMeshUserData';

export default class GeometryCommand extends InputCommand {

    #document: Document;
    parent: THREE.Object3D | null = null;
    protected lastPersistedEntityUuid: UUID | null = null;

    constructor(    id: CommandIdEnum, 
                    commandStack: CommandStack,
                    canvasInputListener: ICanvasInputListener,
                    document: Document) {
        super(id, commandStack, canvasInputListener);
        this.#document = document;
    }

    async updateEntity(sceneObject: ISceneObject): Promise<boolean> {
        const result = await useServicesState.getState().dataProvider?.updateSceneObject(
            sceneObject.data.uuid,
            sceneObject.data.clone()
        );

        if (result) {
            sceneObject.geometry.update(sceneObject.data, true);
        }

        return result != null;
    }

    async persistEntity(sceneObjectData: SceneObjectData) : Promise<boolean> {
        const data = sceneObjectData.clone();
        if (this.parent) {
            const userData = this.parent.userData as ProductMeshUserData;
            if (userData && userData.product) {
                data.parentId = userData.product.globalId;
                this.convertToParentCoordinateSystem(data); // Convert to local coordinate system of the parent
            }
            else {
                console.warn("GeometryCommand.persistEntity: Parent does not have valid product user data.");
            }
        }
        
        const result = await useServicesState.getState().dataProvider?.addSceneObject(this.#document.documentUuid, data);

        if (result) {
            this.lastPersistedEntityUuid = result.uuid;
            const documentObject = createSceneObject(result); // Create a new scene object with the DTO
            this.addToDocument(documentObject);
        }

        return result != null;
    }

    async removeEntity(sceneObjectUuid: UUID) : Promise<boolean> {
        
        const sceneObject = this.#document.getSceneObject(sceneObjectUuid);
        if (!sceneObject) {
            console.error("GeometryCommand.removeEntity: Scene object not found in document.");
            return false;
        }

        const result = await useServicesState.getState().dataProvider?.removeSceneObject(sceneObjectUuid);
        if (result) {
            this.removeFromDocument(sceneObject);
        }
        else {
            console.error("GeometryCommand.removeEntity: Failed to remove scene object from backend.");
        }

        return result ?? false;
    }

    addToDocument(sceneObject: ISceneObject) {
        this.#document.addToDocument(sceneObject);
    }

    removeFromDocument(sceneObject: ISceneObject) {
        this.#document.removeFromDocument(sceneObject);
    }

    getDocument() : Document {
        return this.#document;
    }

    setParent(parent: THREE.Object3D | null) {
       
        if (!parent) {
            this.parent = null;
            return;
        }

        // Prefer to find storey, but if it does not exist, then find building
        const storey = getParentThreeProductByType(parent, ElementTypeEnum.Storey);

        this.parent = storey 
                        ? storey 
                        : getParentThreeProductByType(parent, ElementTypeEnum.Building);
    }

    convertToParentCoordinateSystem(data: SceneObjectData): void {
        if (!this.parent) {
            console.warn("GeometryCommand.convertToParentCoordinateSystem: Parent is not set, cannot convert to parent coordinate system.");
            return;
        }

        // Convert the data to the local coordinate system of the parent
        data.applyMatrix4(this.parent.matrixWorld.clone().invert());
    }

    canUndoLastAction(): boolean {
        return false;
    }   
    
    undoLastAction(): void {
        console.warn("GeometryCommand.undoLastAction: Undo is not implemented for this command.");
    }

    rightClick(event: MouseEvent, raycaster: THREE.Raycaster, targets: THREE.Object3D[]) : void {
        if (this.canUndoLastAction()) {
            this.undoLastAction();
        }
        else {
            super.rightClick(event, raycaster, targets);
        }
    }

    getSceneObjectByThreeJsObject(threeObject : THREE.Object3D): ISceneObject | undefined {
        
        const sceneObjectData = getSceneObjectDataFromObject3D(threeObject);

        // Find the scene object in the document
        if (sceneObjectData) {
            const sceneObj = this.getDocument().getSceneObject(sceneObjectData.uuid); // Verify that the object is part of the document
            if (sceneObj) 
                return sceneObj;
        }

        return undefined;
    }

}